import React, { useEffect, useState, useRef } from 'react';
import api from '../../../services/api';

const UploadTasks = () => {
  const [file, setFile] = useState(null);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingTasks, setFetchingTasks] = useState(false);
  const [message, setMessage] = useState(null);
  const fileInputRef = useRef(null);

  // Fetch projects on mount
  useEffect(() => {
    fetchProjects();
  }, []);

  // Fetch tasks whenever project changes
  useEffect(() => {
    if (projectId) {
      fetchTasks(projectId);
    } else {
      setTasks([]);
    }
  }, [projectId]);

  const fetchProjects = async () => {
    try {
      const res = await api.get('/projects');
      setProjects(res.data);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    }
  };

  // Fetch tasks by projectId from backend
  const fetchTasks = async (pid) => {
    setFetchingTasks(true);
    try {
      const res = await api.get(`/daily-plans/tasks/by-project?projectId=${pid}`);
      setTasks(res.data);
    } catch (err) {
      console.error('Fetch tasks error:', err);
      setTasks([]);
    } finally {
      setFetchingTasks(false);
    }
  };

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    setFile(selected || null);
    setMessage(null);
  };

  const handleUpload = async () => {
    if (!projectId) {
      setMessage({ type: 'error', text: 'Please select a project before uploading.' });
      return;
    }
    if (!file) {
      setMessage({ type: 'error', text: 'Please choose an Excel file to upload.' });
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('projectId', projectId);

    setLoading(true);
    setMessage(null);

    try {
      await api.post('/daily-plans/tasks/upload-excel', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setMessage({ type: 'success', text: '✅ Tasks uploaded successfully!' });
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';

      // Refresh task list after upload
      await fetchTasks(projectId);
    } catch (err) {
      console.error('Upload failed:', err);
      const errMsg = err?.response?.data?.message || 'Upload failed. Please try again.';
      setMessage({ type: 'error', text: `❌ ${errMsg}` });
    } finally {
      setLoading(false);
    }
  };

  const getPriorityBadge = (priority) => {
    const map = {
      HIGH: { bg: '#fee2e2', color: '#dc2626', label: 'High' },
      MEDIUM: { bg: '#fef9c3', color: '#ca8a04', label: 'Medium' },
      LOW: { bg: '#dcfce7', color: '#16a34a', label: 'Low' },
    };
    const key = (priority || 'MEDIUM').toUpperCase();
    const s = map[key] || map['MEDIUM'];
    return (
      <span style={{
        background: s.bg, color: s.color,
        padding: '3px 10px', borderRadius: '12px',
        fontSize: '12px', fontWeight: 600, letterSpacing: '0.03em',
      }}>
        {s.label}
      </span>
    );
  };

  const selectedProject = projects.find(p => String(p.id) === String(projectId));

  const formatEstimate = (value) => {
    if (!value && value !== 0) return '0:00';

    const hours = Math.floor(value);
    const minutes = Math.round((value - hours) * 60);

    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  };
  
  const handleDelete = async (taskId) => {
  const confirmDelete = window.confirm('Are you sure you want to delete this task?');

  if (!confirmDelete) return; // ❌ cancel

  try {
    await api.put(`/daily-plans/tasks/${taskId}/deactivate`);

    fetchTasks(projectId);
  } catch (err) {
    console.error(err);
    alert('Delete failed');
  }
};
  return (
    <div style={{ padding: '28px 32px', fontFamily: 'Segoe UI, sans-serif', color: '#1e293b' }}>

      {/* ── Upload Card ── */}
      <div style={{
        background: '#fff', borderRadius: '12px', padding: '28px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: '28px', maxWidth: '680px',
      }}>

        {/* Project Select */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontWeight: 600, fontSize: '14px', marginBottom: '8px', color: '#374151' }}>
            Select Project
          </label>
          <select
            value={projectId}
            onChange={(e) => { setProjectId(e.target.value); setMessage(null); }}
            style={{
              width: '100%', padding: '10px 14px',
              border: '1px solid #d1d5db', borderRadius: '8px',
              fontSize: '14px', color: projectId ? '#1e293b' : '#9ca3af',
              background: '#fff', outline: 'none', cursor: 'pointer',
            }}
          >
            <option value="">-- Select Project --</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>

        {/* File Input */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontWeight: 600, fontSize: '14px', marginBottom: '8px', color: '#374151' }}>
            Choose Excel File
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            style={{
              display: 'block', width: '100%', padding: '8px 12px',
              border: '1px solid #d1d5db', borderRadius: '8px',
              fontSize: '14px', background: '#f9fafb',
              cursor: 'pointer', boxSizing: 'border-box',
            }}
          />
          {file && (
            <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#6b7280' }}>
              📄 {file.name} ({(file.size / 1024).toFixed(1)} KB)
            </p>
          )}
        </div>

        {/* Feedback Message */}
        {message && (
          <div style={{
            padding: '10px 14px', borderRadius: '8px', marginBottom: '16px', fontSize: '14px',
            background: message.type === 'success' ? '#f0fdf4' : '#fef2f2',
            color: message.type === 'success' ? '#16a34a' : '#dc2626',
            border: `1px solid ${message.type === 'success' ? '#bbf7d0' : '#fecaca'}`,
          }}>
            {message.text}
          </div>
        )}

        {/* Upload Button */}
        <button
          onClick={handleUpload}
          disabled={loading}
          style={{
            background: loading ? '#93c5fd' : '#2563eb',
            color: '#fff', padding: '11px 24px', borderRadius: '8px',
            border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '14px', fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: '8px',
            transition: 'background 0.2s',
          }}
        >
          {loading
            ? <><span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>⏳</span> Uploading...</>
            : <>🚀 Upload Tasks</>
          }
        </button>
      </div>

      {/* ── Task List ── */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
          <span style={{ fontSize: '18px' }}>📋</span>
          <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 700 }}>
            {selectedProject ? `Tasks — ${selectedProject.name}` : 'Uploaded Tasks'}
          </h3>
          {tasks.length > 0 && (
            <span style={{
              background: '#e0e7ff', color: '#4338ca',
              padding: '2px 10px', borderRadius: '12px',
              fontSize: '12px', fontWeight: 600,
            }}>
              {tasks.length} tasks
            </span>
          )}
          {fetchingTasks && (
            <span style={{ fontSize: '12px', color: '#94a3b8', marginLeft: '4px' }}>
              Loading...
            </span>
          )}
        </div>

        <div style={{
          background: '#fff', borderRadius: '12px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                {['#', 'Task', 'Project', 'Priority', 'Estimate', 'Action'].map((h) => (
                  <th key={h} style={{
                    padding: '13px 16px', textAlign: 'left',
                    fontWeight: 700, color: '#475569',
                    fontSize: '13px', letterSpacing: '0.04em', textTransform: 'uppercase',
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!projectId ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', padding: '40px', color: '#94a3b8', fontSize: '14px' }}>
                    Select a project to see its tasks.
                  </td>
                </tr>
              ) : fetchingTasks ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', padding: '40px', color: '#94a3b8', fontSize: '14px' }}>
                    ⏳ Loading tasks...
                  </td>
                </tr>
              ) : tasks.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', padding: '40px', color: '#94a3b8', fontSize: '14px' }}>
                    No tasks found. Upload an Excel file to add tasks.
                  </td>
                </tr>
              ) : (
                tasks.map((t, i) => (
                  <tr
                    key={t.id || i}
                    style={{
                      borderBottom: '1px solid #f1f5f9',
                      background: i % 2 === 0 ? '#fff' : '#fafafa',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f0f9ff'}
                    onMouseLeave={(e) => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fafafa'}
                  >
                    <td style={{ padding: '12px 16px', color: '#94a3b8', fontSize: '13px' }}>{i + 1}</td>
                    <td style={{ padding: '12px 16px', fontWeight: 500, color: '#1e293b' }}>
                      {t.title || '—'}
                    </td>
                    {/* Backend returns t.project directly (PlannedTask has projectId → Project) */}
                    <td style={{ padding: '12px 16px', color: '#475569' }}>
                      {t.project?.name || selectedProject?.name || '—'}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      {getPriorityBadge(t.priority)}
                    </td>
                    <td style={{ padding: '12px 16px', color: '#475569' }}>
                      {formatEstimate(t.estimate ?? 1)}h
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <button
                        onClick={() => handleDelete(t.id)}
                        style={{
                          background: '#ef4444',
                          color: 'white',
                          border: 'none',
                          padding: '5px 10px',
                          borderRadius: '6px',
                          cursor: 'pointer'
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default UploadTasks;